import React, { useState } from "react";
import { observer } from "mobx-react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { IOrgConfig } from "../../../../../models/currentUser/OrgConfig";
import { SubtitleDescription } from "../../../subtitleDescription";
import { CheckboxInput } from "@fidget/common/components/form/checkboxInput";
import { TimePicker } from "@fidget/common/components/form/timePicker/timePicker";
import { DiscardApplyButtons } from "../../../discardApplyButtons";
import styles from "../../../../../containers/settings/settingsEditCommon.css";
import * as localStyles from "./snoozeAndCloseForm.css";
import {
  durationToSeconds,
  isTimeInvalid
} from "@fidget/common/components/form/timePicker/timePickerUtils";
import { staticConfig } from "@fidget/common/config/static-config";

type Time = number | undefined;
type FieldName = keyof Pick<IOrgConfig, "autoCloseTimeoutSeconds" | "autoSnoozeTimeoutSeconds">;
type Fields = Record<FieldName, { time: Time; invalid: boolean | undefined }>;

interface TimeConfigFieldProp {
  min?: Duration;
  max?: Duration;
}

interface Props {
  orgConfig: IOrgConfig;
  snoozeConfig?: TimeConfigFieldProp;
  closeConfig?: TimeConfigFieldProp;
}

export const SnoozeAndCloseForm = observer((props: Props) => {
  const fieldsConfig = initFieldsConfig(props);
  const initialFields: Fields = {
    autoSnoozeTimeoutSeconds: {
      time: fieldsConfig.autoSnoozeTimeoutSeconds.initialTime,
      invalid: isTimeInvalid(
        fieldsConfig.autoSnoozeTimeoutSeconds.initialTime,
        fieldsConfig.autoSnoozeTimeoutSeconds.config
      )
    },
    autoCloseTimeoutSeconds: {
      time: fieldsConfig.autoCloseTimeoutSeconds.initialTime,
      invalid: isTimeInvalid(
        fieldsConfig.autoCloseTimeoutSeconds.initialTime,
        fieldsConfig.autoCloseTimeoutSeconds.config
      )
    }
  };

  const [formFields, setFormFields] = useState<Fields>(initialFields);

  const clearForm = () => {
    setFormFields(initialFields);
  };

  const setTime = (fieldName: FieldName) => (time: Time) => {
    setFormFields((old) => ({
      ...old,
      [fieldName]: { time, invalid: isTimeInvalid(time, fieldsConfig[fieldName].config) }
    }));
  };

  const toggleDisabled = (fieldName: FieldName) => (checked: boolean) => {
    const timeToSet =
      fieldsConfig[fieldName].initialTime === undefined
        ? durationToSeconds(fieldsConfig[fieldName].config.min)
        : fieldsConfig[fieldName].initialTime;
    setFormFields((old) => ({
      ...old,
      [fieldName]: {
        time: checked ? timeToSet : undefined,
        invalid: isTimeInvalid(timeToSet, fieldsConfig[fieldName].config)
      }
    }));
  };

  const submitForm = () => {
    props.orgConfig.setAutoSnoozeAndCloseTimeouts(
      formFields.autoSnoozeTimeoutSeconds.time,
      formFields.autoCloseTimeoutSeconds.time
    );
  };

  const isFormDirty = Object.entries(formFields).some(
    ([key, field]) => field.time !== fieldsConfig[key as FieldName].initialTime
  );
  const isFormInvalid = Object.values(formFields).some((field) => field.invalid);

  return (
    <>
      <div className={styles.title}>
        {intl.formatMessage("settings.assignemntRules.chatAutoSnoozeAndClose.title")}
      </div>
      <div className={styles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.assignmentRules.chatAutoSnooze.title")}
          description={intl.formatMessage("settings.assignmentRules.chatAutoSnooze.description")}
        />
        <div className={styles.content}>
          <CheckboxInput
            id="settings.assignmentRules.chatAutoSnooze.checkbox"
            onChange={toggleDisabled("autoSnoozeTimeoutSeconds")}
            value={formFields.autoSnoozeTimeoutSeconds.time !== undefined}
            label={intl.formatMessage("settings.assignmentRules.chatAutoSnooze.checkbox.label")}
          />
          <h5 className={localStyles.timeInputLabel}>
            {intl.formatMessage("settings.assignmentRules.timeLimitLabel")}
          </h5>
          <TimePicker
            {...formFields.autoSnoozeTimeoutSeconds}
            onTimePicked={(time) => {
              setTime("autoSnoozeTimeoutSeconds")(time);
            }}
            disabled={formFields.autoSnoozeTimeoutSeconds.time === undefined}
          />
        </div>
      </div>
      <div className={styles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.assignmentRules.chatAutoClose.title")}
          description={intl.formatMessage("settings.assignmentRules.chatAutoClose.description")}
        />
        <div className={styles.content}>
          <CheckboxInput
            id="settings.assignmentRules.chatAutoClose.checkbox"
            onChange={toggleDisabled("autoCloseTimeoutSeconds")}
            value={formFields.autoCloseTimeoutSeconds.time !== undefined}
            label={intl.formatMessage("settings.assignmentRules.chatAutoClose.checkbox.label")}
          />
          <h5 className={localStyles.timeInputLabel}>
            {intl.formatMessage("settings.assignmentRules.timeLimitLabel")}
          </h5>
          <TimePicker
            {...formFields.autoCloseTimeoutSeconds}
            onTimePicked={(time) => {
              setTime("autoCloseTimeoutSeconds")(time);
            }}
            disabled={formFields.autoCloseTimeoutSeconds.time === undefined}
          />
        </div>
      </div>
      <div className={styles.section}>
        <DiscardApplyButtons
          allDisabled={!isFormDirty}
          applyDisabled={isFormInvalid}
          onDiscard={clearForm}
          onApply={submitForm}
          alignToLeft
        />
      </div>
    </>
  );
});

interface TimeConfigField {
  config: TimeConfigFieldProp;
  initialTime: Time;
}
type TimeConfig = Record<FieldName, TimeConfigField>;

const initFieldsConfig = (props: Props): TimeConfig => {
  return {
    autoSnoozeTimeoutSeconds: {
      config: props.snoozeConfig || staticConfig.snoozeDefaultConstraints,
      initialTime: props.orgConfig.autoSnoozeTimeoutSeconds
    },
    autoCloseTimeoutSeconds: {
      config: props.closeConfig || staticConfig.closeDefaultConstraints,
      initialTime: props.orgConfig.autoCloseTimeoutSeconds
    }
  };
};
